import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { DocumentAccessCredentialsModel } from '@app/data-layer/models/documents';
import { OrganizationModel } from '@app/data-layer/models/organizations/organization-model';
import { DocumentsRepository } from '@app/data-layer/repositories';
import { OrganizationLogoRepository } from '@app/data-layer/repositories/organization-logo.repository';
import { OrganizationsService } from '@services/organizations.service';
import { Observable, ReplaySubject, EMPTY, of } from 'rxjs';
import { switchMap, take, map, tap, catchError, takeUntil } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'organization-details-edit',
  templateUrl: './organization-details-edit.component.html',
  styleUrls: ['./organization-details-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationDetailsEditComponent implements OnInit, OnDestroy {
  @Input() public formGroup: any;
  @Input() public isEditView: boolean;

  public organizationId: number;
  public delayedUploadFile: File;
  public isLoadingSaveChanges: boolean;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject();

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private documentsRepository: DocumentsRepository,
    private organizationsService: OrganizationsService,
    private organizationLogoRepository: OrganizationLogoRepository,
  ) {}

  ngOnInit(): void {
    this.organizationId = this.formGroup?.get('id').value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public redirectToOrganizationPage() {
    this.router.navigate(['/companies', 'account-settings', 'organization']);
  }

  public updateOrganizationDetails(): void {
    this.isLoadingSaveChanges = true;

    this.updateOrganization()
      .pipe(
        switchMap(() => {
          return !!this.delayedUploadFile ? this.uploadLogo(this.delayedUploadFile) : of({});
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.redirectToOrganizationPage());
  }

  public updateLogoControl(file: File): void {
    if (!this.organizationId) {
      this.delayedUploadFile = file;
      return;
    }

    this.uploadLogo(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe((organization: OrganizationModel) => {
        this.formGroup.get('logoUri').patchValue(organization.logoUri);
        this.cdr.markForCheck();
      });
  }

  public removeLogo(): void {
    if (!this.organizationId) {
      this.formGroup.get('logoUri').patchValue(null);
      return;
    }
    this.organizationLogoRepository
      .deleteUrl(this.organizationId)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe(() => {
        this.formGroup.get('logoUri').patchValue(null);
      });
  }

  public uploadLogo(file: File): Observable<OrganizationModel> {
    return this.organizationLogoRepository.getCredentials(this.organizationId).pipe(
      switchMap((credentials: DocumentAccessCredentialsModel) => {
        return this.documentsRepository.uploadFileByUrl(credentials.uploadUrl, file, false).pipe(map(() => credentials));
      }),
      switchMap((credentials) => {
        return this.organizationLogoRepository.setUrl(this.organizationId, credentials.downloadUrl);
      }),
      switchMap(() => this.organizationsService.getOrganizationByIdFromServer(this.organizationId)),
      take(1),
    );
  }

  private updateOrganization(): Observable<OrganizationModel> {
    const value: OrganizationModel = this.formGroup.getRawValue() as OrganizationModel;

    return this.organizationsService.updateOrganization(value).pipe(
      catchError((error: HttpErrorResponse) => {
        this.organizationsService.clearStateOrganization();
        if (error.status >= 400 && error.status < 500) {
          const contactInformationFormGroup = this.formGroup?.get('contactInformation') as FormGroup;
          const businessEmailControl = contactInformationFormGroup.get('businessEmail');
          businessEmailControl.setErrors({ invalidEmail: true });
          this.resetLoader();
        }
        return EMPTY;
      }),
    );
  }

  private resetLoader() {
    this.isLoadingSaveChanges = false;
    this.cdr.markForCheck();
  }
}
